import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { eventCombo, modalOpen } from "../hooks/useHotkeys.js";
import styles from "../Modules/CommandPalette.module.css";

/**
 * App-wide keyboard help ("?" when not typing) plus the Alt+N "new record"
 * shortcut on list pages (clicks the page's AddButton). Mounted once in
 * Routes.js while signed in. WhatsApp Inbox keeps its own "?" sheet.
 */
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? "⌘" : "Ctrl";

const ROWS = [
    [`${MOD} K`, "Go to any page"],
    ["/", "Go to any page (when not typing)"],
    ["Alt N", "New record (on a list page)"],
    ["Enter", "Next field"],
    ["Enter on the last field", "Save the form"],
    ["↑ ↓  Enter", "Move and pick in a dropdown"],
    ["Esc", "Close a dialog, menu or picker"],
    ["?", "Show this list"],
];

const isTyping = (el) => !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

export default function ShortcutsSheet() {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const closeRef = useRef(null);
    const ownSheet = pathname.startsWith("/whatsapp");

    useEffect(() => {
        const onKey = (e) => {
            if (e.defaultPrevented) return;
            const combo = eventCombo(e);
            if (combo === "alt+n" && !modalOpen()) {
                // The page's "create" button: the one that goes to a /add route,
                // else the only AddButton. Several others (Bulk Contacts beside
                // Add New) → no guess.
                const adds = [...document.querySelectorAll("main [data-add-button]")];
                const add = adds.find((a) => /\/add(\?|$)/.test(a.getAttribute("href") || "")) || (adds.length === 1 ? adds[0] : null);
                if (add) { e.preventDefault(); add.click(); }
                return;
            }
            if (e.key === "?" && !ownSheet && !isTyping(e.target) && !modalOpen()) {
                e.preventDefault();
                setOpen((o) => !o);
            } else if (e.key === "Escape" && open) {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, ownSheet]);

    useEffect(() => { if (open) closeRef.current?.focus(); }, [open]);

    if (!open) return null;
    return (
        <div className={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
            <div className={styles.panel} role="dialog" aria-modal="true" aria-labelledby="shortcuts-title" data-hotkey-scope="modal">
                <div className={styles.searchRow}>
                    <h2 id="shortcuts-title" style={{ margin: 0, fontSize: "var(--fs-section)", fontWeight: 600, flex: 1 }}>Keyboard shortcuts</h2>
                    <button ref={closeRef} type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(false)}>Close</button>
                </div>
                <dl className="shortcut-list">
                    {ROWS.map(([keys, what]) => (
                        <React.Fragment key={keys}>
                            <dt>{keys.split("  ").map((k) => <kbd key={k} className={styles.kbd}>{k}</kbd>)}</dt>
                            <dd>{what}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            </div>
        </div>
    );
}
